import { useState } from 'react';
import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import type { Scores } from '../data/types';
import { ARCHETYPES } from '../data/archetypes';
import { rankedArchetypes, topArchetypes } from '../lib/scoring';
import { copyToClipboard, makeShareURL } from '../lib/share';
import { Constellation } from './Constellation';
import styles from './Result.module.css';

interface ResultProps {
  scores: Scores;
  name: string;
  onNameChange: (name: string) => void;
  saved: boolean;
  onSave: () => void;
  onRetake: () => void;
}

export function Result({ scores, name, onNameChange, saved, onSave, onRetake }: ResultProps) {
  const [feedback, setFeedback] = useState<string | null>(null);
  const ranked = rankedArchetypes(scores);
  const shown = topArchetypes(scores, 40);
  // If nothing crossed 40, show the top one anyway so the page has substance
  const display = shown.length > 0 ? shown : ranked.slice(0, 2);

  function ping(msg: string) {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 1800);
  }

  async function handleShare() {
    const url = makeShareURL(scores, name);
    const ok = await copyToClipboard(url);
    ping(ok ? 'share link copied' : 'copy failed — select the URL manually');
  }

  function handlePrint() {
    window.print();
  }

  return (
    <article className={styles.page}>
      <header className={styles.header}>
        <p className={styles.kicker}>your constellation</p>
        <input
          className={styles.nameInput}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          maxLength={60}
          aria-label="constellation name"
        />
        <p className={styles.note}>
          a starting point, not a verdict. revisit when something shifts.
        </p>
      </header>

      <Constellation scores={scores} />

      {/* Text-equivalent for screen readers */}
      <ol className={styles.srOnly}>
        {ranked.map((a) => (
          <li key={a}>
            {ARCHETYPES[a].label}: {scores[a]} of 100
          </li>
        ))}
      </ol>

      <section className={styles.interpretations}>
        <h2 className={styles.sectionTitle}>what shows up most strongly</h2>
        {display.map((arc) => {
          const def = ARCHETYPES[arc];
          return (
            <article key={arc} className={styles.archetype}>
              <header className={styles.archetypeHeader}>
                <span className={styles.dot} style={{ background: def.accent }} />
                <h3 className={styles.archetypeLabel}>{def.label}</h3>
                <span className={styles.archetypeScore}>{scores[arc]}</span>
              </header>
              <p className={styles.essence}>{def.essence}</p>
              <p className={styles.body}>{def.feels}</p>
              <p className={styles.body}>{def.practice}</p>
            </article>
          );
        })}
      </section>

      <footer className={styles.actions}>
        {!saved ? (
          <button type="button" className={styles.primary} onClick={onSave}>
            save this constellation
          </button>
        ) : (
          <span className={styles.savedBadge}>✓ saved</span>
        )}
        <button type="button" className={styles.action} onClick={handlePrint}>
          print
        </button>
        <button type="button" className={styles.action} onClick={handleShare}>
          copy share link
        </button>
        <button type="button" className={styles.action} onClick={onRetake}>
          take it again
        </button>
      </footer>

      {feedback && (
        <div className={styles.feedback} role="status" aria-live="polite">
          {feedback}
        </div>
      )}

      <RelatedApps apps={RELATED_BY_APP['archetype-mirror']} surface="cream" />
    </article>
  );
}
